import { BugReportModal } from '@/components/global/bug-reports/BugReportModal';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import { TriangleAlert } from 'lucide-react';

/**
 * Floating, gently pulsating "report a bug" button. Mirrors the
 * KnowledgeBaseBubble design (icon disc that expands into a label on hover),
 * but yellow and non-dismissible — it is always present and simply opens the
 * bug-report modal on click. Desktop only. Sits just above the KB bubble.
 *
 * The button is wired as the BugReportModal trigger, so all submit logic and
 * the `bug-reports.create` permission gate are reused as-is.
 */
export function BugReportBubble() {
    const { t } = useTranslations();
    const label = t('navigation.bug_bubble.label', 'Report a bug');

    return (
        <div className="fixed right-6 bottom-23 z-50 hidden md:block">
            <BugReportModal
                trigger={
                    <button
                        type="button"
                        aria-label={t('navigation.bug_bubble.aria_label', 'Report a bug')}
                        className={cn(
                            'group bg-warning text-warning-foreground flex h-14 items-center overflow-hidden rounded-full shadow-lg',
                            'transition-[width,padding] duration-300 ease-out',
                            'w-14 group-hover:w-auto group-hover:pr-5',
                            'focus-visible:ring-ring focus-visible:ring-offset-background focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
                        )}
                    >
                        {/* Pulsing halo — sits behind the solid icon disc so the icon stays crisp */}
                        <span className="relative flex size-14 shrink-0 items-center justify-center">
                            <span className="animate-kb-pulse bg-warning absolute inline-flex size-12 rounded-full motion-reduce:animate-none" />
                            <TriangleAlert className="relative size-6" />
                        </span>

                        <span className="max-w-0 text-sm font-medium whitespace-nowrap opacity-0 transition-[max-width,opacity] duration-300 ease-out group-hover:max-w-xs group-hover:opacity-100">
                            {label}
                        </span>
                    </button>
                }
            />
        </div>
    );
}
